import { Button, Stack } from '@mui/material';
import {
  ConductEntry,
  GET_CONDUCT_ENTRIES_QUERY,
  GetConductEntriesQueryFilters,
  SORT_DIRECTION,
  useGetConductEntriesQuery,
} from '@schooly/api';
import { useAuth } from '@schooly/components/authentication';
import {
  CommentRowCell,
  DropdownComments,
  EyeIcon,
  GridCell,
  GridRow,
  SkeletonRows,
} from '@schooly/style';
import { FC, useCallback, useEffect, useMemo } from 'react';
import { FormattedMessage } from 'react-intl';
import { useNavigate } from 'react-router-dom';

import useAppLocation from '../../hooks/useAppLocation';
import { commentToDropdownCommentItem } from '../../utils/convertComments';
import { ConductEntryRow, SKELETON_COLS } from './ConductGrid';
import { useConductComments } from './useConductComments';

const CHILD_PAGE_SIZE = 10;

type ConductStudentEntriesProps = {
  schoolId: string;
  relationId: string;
  filters: GetConductEntriesQueryFilters;
  canEdit: boolean;
  hoverId: string | null;
  onHover: (id: string | null) => void;
  isLastStudent: boolean;
  totalEntries: number;
};

export const ConductStudentEntries: FC<ConductStudentEntriesProps> = ({
  schoolId,
  relationId,
  filters,
  canEdit,
  hoverId,
  onHover,
  isLastStudent,
  totalEntries,
}) => {
  const { currentStaff } = useAuth();
  const navigate = useNavigate();
  const location = useAppLocation();

  const childFilters = useMemo<GetConductEntriesQueryFilters>(
    () => ({ ...filters, student: [relationId] }),
    [filters, relationId],
  );

  const childParams = useMemo(
    () => ({
      schoolId,
      pageSize: CHILD_PAGE_SIZE,
      filters: childFilters,
      sort: { columnTextId: 'date' as const, direction: SORT_DIRECTION.DESC },
      query: '',
    }),
    [schoolId, childFilters],
  );

  const { data, isLoading, hasNextPage, fetchNextPage, isFetchingNextPage, setParams } =
    useGetConductEntriesQuery(childParams, { refetchOnMount: 'always' });

  useEffect(() => {
    setParams(childParams);
  }, [childParams, setParams]);

  const entries = useMemo(() => data?.pages.flatMap((p) => p.results) ?? [], [data?.pages]);

  const queryKey = useMemo(() => [GET_CONDUCT_ENTRIES_QUERY, childParams], [childParams]);

  const { handleCommentAdd, handleCommentEdit, pendingEntriesId } = useConductComments({
    currentStaff,
    queryKey,
  });

  const handleClickEntry = useCallback(
    (entry: ConductEntry) => {
      if (!canEdit) return;
      navigate(`/conduct/${entry.id}/edit`, {
        state: { backgroundLocation: location },
      });
    },
    [canEdit, location, navigate],
  );

  return (
    <>
      {entries.map((entry, index) => {
        const isLast = isLastStudent && index === entries.length - 1 && !hasNextPage;

        return (
          <ConductEntryRow
            key={entry.id}
            schoolId={schoolId}
            entry={entry}
            hideStudentName
            hoverIdStudentId={hoverId}
            isLast={isLast}
            isEditable={canEdit}
            onClick={() => handleClickEntry(entry)}
            onMouseEnter={() => onHover(entry.student.relation_id)}
            onMouseLeave={() => onHover(null)}
            commentsCellContent={
              <CommentRowCell rowStyleProps={{ my: -1 }}>
                {(onToggle, cellRef) => (
                  <DropdownComments
                    comments={entry.comments.map(commentToDropdownCommentItem)}
                    onAdd={handleCommentAdd(entry)}
                    onEdit={handleCommentEdit(entry)}
                    disabled={pendingEntriesId.has(entry.id)}
                    canAdd={canEdit}
                    canEditOwn={canEdit}
                    canEditOther={canEdit}
                    getParentRef={() => cellRef}
                    onToggle={(val) => {
                      onHover('');
                      onToggle(val);
                    }}
                    popoverMargin={2}
                    currentStaff={currentStaff}
                  />
                )}
              </CommentRowCell>
            }
          />
        );
      })}

      {(isLoading || isFetchingNextPage) && (
        <SkeletonRows
          columnsCount={SKELETON_COLS}
          amount={Math.max(0, Math.min(totalEntries - entries.length, 10))}
        />
      )}

      {hasNextPage && !isFetchingNextPage && (
        <GridRow>
          <GridCell noVerticalPadding colSpan={10}>
            <Stack
              sx={{ width: '100%', cursor: 'pointer' }}
              py={1}
              alignItems="center"
              onClick={() => fetchNextPage()}
            >
              <Button startIcon={<EyeIcon />} variant="text">
                <FormattedMessage id="action-ShowMoreButton" />
              </Button>
            </Stack>
          </GridCell>
        </GridRow>
      )}
    </>
  );
};
